/**
 * InterpreterMessaging Component
 *
 * Real-time messaging interface for Admin/SuperAdmin/Scheduler to communicate with interpreters.
 *
 * Features:
 * - SignalR real-time messaging
 * - Message pagination (load older messages)
 * - Typing indicators
 * - Read receipts
 * - Offline/reconnection handling
 * - Optimistic UI updates
 *
 * Props:
 * - interpreterId: Guid - The interpreter's IntId
 * - interpreterUserId: Guid - The interpreter's linked UserId (required for messaging)
 * - interpreterName: string - Display name for the interpreter
 * - currentUserId: Guid - The current user's UserId
 * - token: string - JWT authentication token
 */

import React, { Component } from 'react';
import { Input, Button, Spinner, Alert } from 'reactstrap';
import { InterpreterMessagingClient } from './InterpreterMessagingClient';
import {
  getOrCreateDirectConversation,
  getMessages,
  sendMessage,
  markMessagesAsRead,
  getErrorMessage,
} from './InterpreterMessagingApi';
import './InterpreterMessaging.css';

export class InterpreterMessaging extends Component {
  static displayName = InterpreterMessaging.name;

  constructor(props) {
    super(props);

    this.state = {
      // Conversation state
      conversation: null,
      messages: [],
      messageText: '',

      // Loading states
      isLoading: true,
      isSending: false,
      isLoadingMore: false,

      // Error state
      error: null,

      // Pagination
      hasMore: true,

      // Real-time state
      isConnected: false,
      typingUsers: [],
      connectionState: 'disconnected',
    };

    // SignalR client instance
    this.messagingClient = null;

    // Refs
    this.messagesEndRef = React.createRef();
    this.messagesContainerRef = React.createRef();

    // Timers
    this.typingTimeout = null;
    this.markAsReadDebounce = null;
  }

  componentDidMount() {
    // Initialize conversation and connect to SignalR
    this.initializeConversation();
  }

  componentWillUnmount() {
    // Cleanup
    this.cleanup();
  }

  /**
   * Cleanup method called on unmount
   */
  cleanup = () => {
    // Clear timers
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }

    if (this.markAsReadDebounce) {
      clearTimeout(this.markAsReadDebounce);
      this.markAsReadDebounce = null;
    }

    // Disconnect SignalR
    if (this.messagingClient) {
      const { conversation } = this.state;
      if (conversation) {
        this.messagingClient.leaveConversation(conversation.conversationId);
      }
      this.messagingClient.disconnect();
      this.messagingClient = null;
    }
  };

  /**
   * Initialize conversation with interpreter
   */
  initializeConversation = async () => {
    const { interpreterUserId, token } = this.props;

    try {
      this.setState({ isLoading: true, error: null });

      // Get or create direct conversation
      const result = await getOrCreateDirectConversation(token, interpreterUserId);
      const conversation = result.data;

      this.setState({ conversation });

      // Load initial messages
      await this.loadMessages();

      // Connect SignalR and join conversation
      await this.connectSignalR();
      if (this.messagingClient && conversation) {
        await this.messagingClient.joinConversation(conversation.conversationId);
      }

      // Mark messages as read
      this.handleMarkAsRead();
    } catch (error) {
      this.setState({
        error: getErrorMessage(error),
        isLoading: false,
      });
    }
  };

  /**
   * Connect to SignalR hub
   */
  connectSignalR = async () => {
    const { token } = this.props;

    try {
      this.messagingClient = new InterpreterMessagingClient(token);

      // Subscribe to events
      this.messagingClient.onNewMessage(this.handleNewMessage);
      this.messagingClient.onTyping(this.handleTypingUpdate);
      this.messagingClient.onConnectionChange(this.handleConnectionStateChange);
      this.messagingClient.onError(this.handleSignalRError);

      // Connect
      await this.messagingClient.connect();
    } catch (error) {
      console.error('Failed to connect to SignalR:', error);
      // Don't fail the whole component, just continue without real-time
    }
  };

  /**
   * Load messages with optional pagination
   */
  loadMessages = async (loadMore = false) => {
    const { token } = this.props;
    const { conversation, messages } = this.state;

    if (!conversation) return;

    try {
      if (loadMore) {
        this.setState({ isLoadingMore: true });
      }

      const filter = {
        conversationId: conversation.conversationId,
        pageSize: 20,
      };

      // Add cursor for pagination
      if (loadMore && messages.length > 0) {
        const oldestMessage = messages[0];
        filter.cursorMessageId = oldestMessage.messageId;
        filter.cursorSentAt = oldestMessage.sentAt;
      }

      const result = await getMessages(token, filter);
      const messageList = result.data?.messages || [];

      this.setState((prev) => ({
        messages: loadMore ? [...messageList, ...prev.messages] : messageList,
        hasMore: result.data?.hasMore || false,
        isLoading: false,
        isLoadingMore: false,
      }));

      // Scroll to bottom on initial load
      if (!loadMore) {
        setTimeout(() => this.scrollToBottom(), 100);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      this.setState({
        error: getErrorMessage(error),
        isLoading: false,
        isLoadingMore: false,
      });
    }
  };

  /**
   * Send a message
   */
  handleSendMessage = async () => {
    const { token, currentUserId } = this.props;
    const { conversation, messageText } = this.state;

    if (!messageText.trim() || !conversation) return;

    const text = messageText.trim();

    // Create optimistic message
    const tempMessage = {
      messageId: `temp-${Date.now()}`,
      conversationId: conversation.conversationId,
      senderUserId: currentUserId,
      messageText: text,
      sentAt: new Date().toISOString(),
      status: 'sending',
    };

    // Optimistic UI update
    this.setState((prev) => ({
      messages: [...prev.messages, tempMessage],
      messageText: '',
      isSending: true,
    }));

    // Stop typing indicator
    if (this.messagingClient) {
      this.messagingClient.stopTyping(conversation.conversationId);
    }

    this.scrollToBottom();

    try {
      // Try SignalR first
      if (this.state.isConnected && this.messagingClient) {
        await this.messagingClient.sendMessage(conversation.conversationId, text);
      } else {
        // Fallback to REST API
        await sendMessage(token, {
          conversationId: conversation.conversationId,
          messageText: text,
        });
      }

      // Update message status to sent
      this.setState((prev) => ({
        messages: prev.messages.map((m) =>
          m.messageId === tempMessage.messageId ? { ...m, status: 'sent' } : m
        ),
        isSending: false,
      }));
    } catch (error) {
      console.error('Failed to send message:', error);

      // Mark message as failed
      this.setState((prev) => ({
        messages: prev.messages.map((m) =>
          m.messageId === tempMessage.messageId
            ? { ...m, status: 'failed', error: getErrorMessage(error) }
            : m
        ),
        isSending: false,
      }));
    }
  };

  /**
   * Retry sending a failed message
   */
  retryMessage = async (message) => {
    const { token } = this.props;
    const { conversation } = this.state;

    if (!conversation) return;

    // Update message status to sending
    this.setState((prev) => ({
      messages: prev.messages.map((m) =>
        m.messageId === message.messageId ? { ...m, status: 'sending', error: null } : m
      ),
    }));

    try {
      // Try SignalR first
      if (this.state.isConnected && this.messagingClient) {
        await this.messagingClient.sendMessage(conversation.conversationId, message.messageText);
      } else {
        // Fallback to REST API
        await sendMessage(token, {
          conversationId: conversation.conversationId,
          messageText: message.messageText,
        });
      }

      // Update message status to sent
      this.setState((prev) => ({
        messages: prev.messages.map((m) =>
          m.messageId === message.messageId ? { ...m, status: 'sent' } : m
        ),
      }));
    } catch (error) {
      console.error('Failed to retry message:', error);

      // Mark message as failed again
      this.setState((prev) => ({
        messages: prev.messages.map((m) =>
          m.messageId === message.messageId
            ? { ...m, status: 'failed', error: getErrorMessage(error) }
            : m
        ),
      }));
    }
  };

  /**
   * Mark messages as read (debounced)
   */
  handleMarkAsRead = () => {
    const { token } = this.props;
    const { conversation, messages } = this.state;

    if (!conversation || messages.length === 0) return;

    // Clear existing debounce timer
    if (this.markAsReadDebounce) {
      clearTimeout(this.markAsReadDebounce);
    }

    // Debounce for 300ms
    this.markAsReadDebounce = setTimeout(async () => {
      try {
        // Get the last message ID (skip temp messages)
        const lastMessage = messages
          .filter((m) => !m.messageId.startsWith('temp-'))
          .slice(-1)[0];

        if (!lastMessage) return;

        await markMessagesAsRead(token, {
          conversationId: conversation.conversationId,
          lastReadMessageId: lastMessage.messageId,
        });
      } catch (error) {
        // Silent fail - marking as read is non-critical
        console.error('Failed to mark messages as read:', error);
      }
    }, 300);
  };

  /**
   * Handle new message from SignalR
   */
  handleNewMessage = (message) => {
    const { conversation } = this.state;

    // Ignore messages for other conversations
    if (!conversation || message.conversationId !== conversation.conversationId) {
      return;
    }

    this.setState((prev) => {
      // Check if this message already exists (optimistic update or duplicate)
      const existingIndex = prev.messages.findIndex(
        (m) =>
          m.messageId === message.messageId ||
          (m.status === 'sending' && m.messageText === message.messageText)
      );

      if (existingIndex >= 0) {
        // Update existing message with server data
        const updatedMessages = [...prev.messages];
        updatedMessages[existingIndex] = { ...message, status: 'sent' };
        return { messages: updatedMessages };
      }

      // New message from other user
      return { messages: [...prev.messages, message] };
    });

    // Auto-scroll to new message
    this.scrollToBottom();

    // Mark as read
    this.handleMarkAsRead();
  };

  /**
   * Handle typing indicator update from SignalR
   */
  handleTypingUpdate = (update) => {
    const { conversation } = this.state;

    // Ignore typing updates for other conversations
    if (!conversation || update.conversationId !== conversation.conversationId) {
      return;
    }

    this.setState((prev) => {
      const typingUsers = new Set(prev.typingUsers);

      if (update.isTyping) {
        typingUsers.add(update.userName);
      } else {
        typingUsers.delete(update.userName);
      }

      return { typingUsers: Array.from(typingUsers) };
    });
  };

  /**
   * Handle connection state change
   */
  handleConnectionStateChange = (state) => {
    this.setState({
      connectionState: state,
      isConnected: state === 'connected',
    });
  };

  /**
   * Handle SignalR error
   */
  handleSignalRError = (error) => {
    console.error('SignalR error:', error);
    // Don't show to user - SignalR errors are usually transient
  };

  /**
   * Scroll to bottom of messages
   */
  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /**
   * Handle scroll event for load more
   */
  handleScroll = (e) => {
    const { scrollTop } = e.target;
    const { hasMore, isLoadingMore } = this.state;

    // Load more when scrolled near top
    if (scrollTop < 100 && hasMore && !isLoadingMore) {
      this.loadMessages(true);
    }
  };

  /**
   * Handle input change
   */
  handleInputChange = (e) => {
    const messageText = e.target.value;
    this.setState({ messageText });

    const { conversation } = this.state;

    // Send typing indicator (debounced)
    if (messageText && conversation && this.messagingClient) {
      if (!this.typingTimeout) {
        this.messagingClient.startTyping(conversation.conversationId);
      }

      // Clear existing timeout
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }

      // Stop typing after 2 seconds of no input
      this.typingTimeout = setTimeout(() => {
        if (this.messagingClient && conversation) {
          this.messagingClient.stopTyping(conversation.conversationId);
        }
        this.typingTimeout = null;
      }, 2000);
    }
  };

  /**
   * Handle key press (send on Enter)
   */
  handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSendMessage();
    }
  };

  /**
   * Format timestamp for display
   */
  formatTime = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    // Format as date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  /**
   * Get connection status message
   */
  getConnectionStatusMessage = () => {
    const { connectionState } = this.state;

    switch (connectionState) {
      case 'connecting':
        return 'Connecting...';
      case 'reconnecting':
        return 'Reconnecting...';
      case 'disconnected':
        return 'No connection - Messages will be sent when reconnected';
      case 'error':
        return 'Connection error - Retrying...';
      default:
        return null;
    }
  };

  render() {
    const { interpreterName, currentUserId } = this.props;
    const {
      messages,
      messageText,
      isLoading,
      isSending,
      isLoadingMore,
      error,
      hasMore,
      isConnected,
      typingUsers,
      connectionState,
    } = this.state;

    // Loading state
    if (isLoading) {
      return (
        <div className="messaging-container messaging-loading">
          <Spinner color="primary" />
          <p>Loading messages...</p>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="messaging-container messaging-error">
          <Alert color="danger">{error}</Alert>
          <Button color="primary" onClick={this.initializeConversation}>
            Retry
          </Button>
        </div>
      );
    }

    const statusMessage = this.getConnectionStatusMessage();

    return (
      <div className="messaging-container">
        {/* Connection status banner */}
        {statusMessage && (
          <div
            className={`messaging-status-banner ${
              connectionState === 'disconnected' || connectionState === 'error'
                ? 'messaging-status-error'
                : 'messaging-status-info'
            }`}
          >
            {connectionState === 'connecting' || connectionState === 'reconnecting' ? (
              <Spinner size="sm" className="messaging-status-spinner" />
            ) : null}
            {statusMessage}
          </div>
        )}

        {/* Messages area */}
        <div
          className="messaging-messages"
          onScroll={this.handleScroll}
          ref={this.messagesContainerRef}
        >
          {/* Load more button */}
          {hasMore && (
            <div className="messaging-load-more">
              {isLoadingMore ? (
                <Spinner size="sm" color="primary" />
              ) : (
                <Button
                  size="sm"
                  color="link"
                  onClick={() => this.loadMessages(true)}
                  disabled={isLoadingMore}
                >
                  Load older messages
                </Button>
              )}
            </div>
          )}

          {/* Empty state */}
          {messages.length === 0 ? (
            <div className="messaging-empty">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            // Message list
            messages.map((message) => {
              const isSent = message.senderUserId === currentUserId;
              const isFailed = message.status === 'failed';
              const isSending = message.status === 'sending';

              return (
                <div
                  key={message.messageId}
                  className={`message-bubble ${isSent ? 'message-sent' : 'message-received'} ${
                    isFailed ? 'message-failed' : ''
                  }`}
                >
                  <div className="message-text">{message.messageText}</div>
                  <div className="message-meta">
                    {this.formatTime(message.sentAt)}
                    {isSending && ' • Sending...'}
                    {isFailed && (
                      <span
                        className="message-retry"
                        onClick={() => this.retryMessage(message)}
                        role="button"
                        tabIndex={0}
                      >
                        {' • Failed. Retry?'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {/* Typing indicator */}
          {typingUsers.length > 0 && (
            <div className="typing-indicator">
              {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
            </div>
          )}

          {/* Scroll anchor */}
          <div ref={this.messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="messaging-input-row">
          <Input
            type="text"
            value={messageText}
            onChange={this.handleInputChange}
            onKeyPress={this.handleKeyPress}
            placeholder={`Message ${interpreterName}...`}
            disabled={isSending}
          />
          <Button
            color="primary"
            onClick={this.handleSendMessage}
            disabled={!messageText.trim() || isSending}
          >
            {isSending ? <Spinner size="sm" /> : 'Send'}
          </Button>
        </div>
      </div>
    );
  }
}
